import {
  Autocomplete,
  CircularProgress,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import React from "react";
import { AddInstrumentModalController, AddInstrumentModalControllerDelegate, IAssignment } from "./controller";

interface State {
  isLoading: boolean;
  isSubmitting: boolean;
  assignmentsLoading: boolean;
  assignmentsInput: string;
  assignments: IAssignment[];
  selectedAssignments: IAssignment[];
  errors: {
    insufficientLicenses: boolean;
  }
}

interface Props {
  userUid?: string;
  delegate: AddInstrumentModalDelegate;
}

export interface AddInstrumentModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  genericError: () => void;
  didAddAssignments: () => void;
}

class AddInstrumentModal extends React.Component<Props, State> implements AddInstrumentModalControllerDelegate {

  controller: AddInstrumentModalController
  private assignmentsFetchDebounce?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      isSubmitting: false,
      assignmentsLoading: false,
      assignmentsInput: "",
      assignments: [],
      selectedAssignments: [],
      errors: {
        insufficientLicenses: false
      }
    }
    this.controller = new AddInstrumentModalController(this);
  }

  // delegate
  didGetAssignments(assignments: IAssignment[]) {
    this.setState({
      assignments
    });
  }

  insufficientLicensesError() {
    this.setState({
      isLoading: false,
      isSubmitting: false,
      errors: {
        insufficientLicenses: true
      }
    })
  }

  didAddAssignments(){
    this.setState({ isSubmitting: false });
    this.props.delegate.didAddAssignments();
  }

  genericError() {
    this.setState({ isSubmitting: false });
    this.props.delegate.genericError();
  };

  applicationShouldLogout() {
    this.setState({ isSubmitting: false });
    this.props.delegate.applicationShouldLogout();
  };

  // internals

  handleChangeSelected(selectedAssignments: IAssignment[]): void {
    this.setState({
      selectedAssignments
    })
  }

  componentDidMount(): void {
    this.fetchAssignments("");
  }

  didClickClose() {
    this.props.delegate.shouldCloseModal();
  }

  didClickAddInstruments() {
    this.setState({
      isSubmitting: true
    });
    const token = localStorage.getItem("AccessToken")!;
    const userUid = this.props.userUid!;
    const idsToAdd = this.state.selectedAssignments.map(
      (assignment) => assignment.applicationId
    );
    this.controller.addAssignmentsToUser(token, userUid, idsToAdd);
  }

  private fetchAssignments(filter: string) {
    this.setState({ assignmentsLoading: true });
    clearTimeout(this.assignmentsFetchDebounce!);
    this.assignmentsFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const normalizedFilter = filter.trim();
        const params = {
          page: 1,
          limit: 15,
          ...(normalizedFilter ? { filter: normalizedFilter } : {}),
        };
        await this.controller.getManagedAssignments(token, {
          ...params,
        });
      } finally {
        this.setState({ assignmentsLoading: false, isLoading: false });
      }
    }, 300);
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderForm() {
    const assignmentsById = new Map<number, IAssignment>();
    this.state.selectedAssignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    this.state.assignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    const assignmentOptions = Array.from(assignmentsById.values());

    return (
      <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
        <Typography color="text.secondary">
          Selecione as aplicações que deseja adicionar ao usuário.
        </Typography>
        <Autocomplete
          multiple
          options={assignmentOptions}
          getOptionLabel={(assignment: IAssignment) =>
            `${assignment.title} (${assignment.instrumentTitle})`
          }
          value={this.state.selectedAssignments}
          onChange={(_, newValue) => this.handleChangeSelected(newValue)}
          inputValue={this.state.assignmentsInput}
          onInputChange={(_, value) => {
            this.setState({ assignmentsInput: value }, () => {
              this.fetchAssignments(value);
            });
          }}
          loading={this.state.assignmentsLoading}
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) =>
            option.applicationId === value.applicationId
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const optionKey = option.applicationId ?? key;
            return (
              <li key={optionKey} {...optionProps}>
                <Checkbox checked={selected} />
                {`${option.title} (${option.instrumentTitle})`}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aplicações"
              helperText="Comece a digitar para buscar as aplicações desejadas."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {this.state.assignmentsLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 200,
            },
          }}
          noOptionsText="Nenhuma aplicação encontrada"
          sx={{
            width: "100%",
          }}
        />
        
      </Box>
    );
  }

  renderInssuficientLicenses() {
    return (
      <Box display="flex" flexDirection="column">
        <Typography color="error" align="center" alignSelf="center" fontWeight="bold">ERRO</Typography>
        <Typography color="error" alignSelf="center">Você não possui licenças disponíveis para todos os instrumentos selecionados e por isso não pode atribuir as aplicações.</Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            this.props.delegate.shouldCloseModal()
          }}
        >
          Voltar
        </Button>
      </Box>
    )
  }

  // Internals
  render() {
    let dataToRender;
    if(this.state.isLoading || this.state.isSubmitting) {
      dataToRender = this.renderLoading()
    } else if (this.state.errors.insufficientLicenses) {
      dataToRender = this.renderInssuficientLicenses()
    } else {
      dataToRender = this.renderForm()
    }

    return (
      <Dialog
        open={!!this.props.userUid}
        onClose={() => this.didClickClose()}
        variant="modal"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="modal">
          <NoteAddIcon/>
          Adicionar aplicações
        </DialogTitle>
        <DialogContent variant="modal">
          {dataToRender}
        </DialogContent>
        {!this.state.isLoading &&
          !this.state.isSubmitting &&
          !this.state.errors.insufficientLicenses && (
          <DialogActions
            variant="modal"
            sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                this.props.delegate.shouldCloseModal()
              }}
            >
              Voltar
            </Button>

            <Button
              variant="contained"
              onClick={() => this.didClickAddInstruments()}
              disabled={this.state.selectedAssignments.length === 0}
            >
              Adicionar Aplicações
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default AddInstrumentModal;
